import { useContext, useState } from "react";
import { FeatureFlagName } from "../../types/feature-flags/featureFlag";
import { FeatureFlagButton } from "../common/FeatureFlags/FeatureFlagButton";
import TranscriptRequestDialog from "./TranscriptRequestDialog";
import { t } from "i18next";
import { useAlert } from "../../context/AlertContext";
import { useTranscriptRequest } from "../../hooks/useTranscription";
import LoadingIndicator from "../common/LoadingIndicator/LoadingIndicator";
import { AuthContext } from "../../context/AuthContext";

export function TranscribeButton({
  className = "",
  customerId,
  interactionId,
  hasExistingTranscript = false,
  onTranscriptionStarted,
}: Readonly<TranscribeButtonProps>) {
  const { mutateAsync: transcribeAsync, isPending } = useTranscriptRequest();
  const showAlert = useAlert();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { user } = useContext(AuthContext);

  const handleRequestTranscript = async () => {
    if (!userHasPermissionToRedact()) {
      const result = await transcribeAsync({
        customerId,
        interactionId,
      });
      onTranscriptionStarted?.(result.jobId);
      showAlert(t("transcription:transcriptRequestSubmitted"), "success");
      return;
    }
    setIsDialogOpen(true);
  };

  function userHasPermissionToRedact(): boolean {
    return user?.hasPermission("interactions:Redact") ?? false;
  }

  return (
    <>
      <FeatureFlagButton
        featureFlag={FeatureFlagName.Transcription}
        className={className}
        variant="contained"
        onClick={handleRequestTranscript}
        disabled={isPending}
      >
        {isPending && (
          <LoadingIndicator
            sx={{
              height: "1.65em !important",
              width: "1.65em !important",
              marginRight: "0.5em",
            }}
          />
        )}
        Transcribe
      </FeatureFlagButton>
      <TranscriptRequestDialog
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        customerId={customerId}
        interactionId={interactionId}
        hasExistingTranscript={hasExistingTranscript}
        onTranscriptionStarted={onTranscriptionStarted}
      />
    </>
  );
}

export interface TranscribeButtonProps {
  className?: string;
  customerId: string;
  interactionId: string;
  hasExistingTranscript?: boolean;
  onTranscriptionStarted?: (jobId: string) => void;
}
